import { useEffect, useState } from "react";
import { Download, X, Share, Plus, Check, Smartphone, Compass, Link2, Chrome, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const DISMISS_KEY = "synesis_install_dismissed";
export const INSTALL_EVENT = "synesis:install-guide";

const Step = ({ n, children }) => (
  <li className="flex items-start gap-3.5">
    <span className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-blue-600 to-indigo-500 text-white text-[15px] font-bold flex items-center justify-center shadow-lg shadow-blue-900/40">
      {n}
    </span>
    <span className="text-[15px] leading-7 text-slate-200 pt-1">{children}</span>
  </li>
);

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [inSafari, setInSafari] = useState(true);
  const [inChrome, setInChrome] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    setInstalled(standalone);
    const ios = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    setIsIOS(ios);
    const ua = window.navigator.userAgent;
    setInSafari(!/(CriOS|FxiOS|EdgiOS|OPiOS|FBAN|FBAV|Instagram|Line|GSA)/i.test(ua));
    setInChrome(/CriOS/i.test(ua) || (/Chrome\//i.test(ua) && !/(EdgA?|OPR|SamsungBrowser|FBAN|FBAV|Instagram)/i.test(ua)));
    setIsAndroid(/android/i.test(ua));

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (!localStorage.getItem(DISMISS_KEY) && !standalone) setOpen(true);
    };
    const onForce = () => setOpen(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener(INSTALL_EVENT, onForce);

    if (ios && !standalone && !localStorage.getItem(DISMISS_KEY)) {
      const t = setTimeout(() => setOpen(true), 1200);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener(INSTALL_EVENT, onForce); };
    }
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener(INSTALL_EVENT, onForce); };
  }, []);

  const close = () => { localStorage.setItem(DISMISS_KEY, "1"); setOpen(false); };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    close();
  };

  const openInSafari = () => {
    const url = window.location.href;
    window.location.href = `x-safari-${url}`;
    setTimeout(() => {
      if (!document.hidden) {
        navigator.clipboard?.writeText(url);
        toast.info("Link copiato", { description: "Apri Safari e incolla l'indirizzo nella barra in alto." });
      }
    }, 1200);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      toast.success("Link copiato", { description: "Incollalo nella barra degli indirizzi del browser." });
    } catch {
      toast.info(window.location.origin, { description: "Copia questo indirizzo e aprilo nel browser." });
    }
  };

  const openInChrome = () => {
    const { host, pathname, search } = window.location;
    const url = window.location.href;
    if (isAndroid) {
      window.location.href = `intent://${host}${pathname}${search}#Intent;scheme=https;package=com.android.chrome;end`;
    } else {
      window.location.href = `googlechromes://${host}${pathname}${search}`;
    }
    setTimeout(() => {
      if (!document.hidden) {
        navigator.clipboard?.writeText(url);
        toast.info("Chrome non si è aperto", { description: "Link copiato: aprilo in Chrome incollandolo nella barra degli indirizzi." });
      }
    }, 1500);
  };

  const showBrowserBar = () => {
    close();
    window.scrollBy({ top: 120, behavior: "smooth" });
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 700);
    toast.info("Fai comparire la barra di Safari", {
      description: "Tocca il bordo in basso dello schermo (o l'indirizzo in alto): riappare la barra con l'icona Condividi. Poi riapri la guida da «Installa app» nel menu.",
      duration: 9000,
    });
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" data-testid="install-prompt">
      <div className="absolute inset-0 bg-[#02060f]/75 backdrop-blur-sm" onClick={close} data-testid="install-scrim" />
      <div className="relative w-full sm:max-w-md max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-white/12 bg-[#0b1226] px-5 pt-7 pb-6 sm:px-7 shadow-[0_-20px_60px_-20px_rgba(0,0,0,0.95)] animate-fade-up">
        <button onClick={close} data-testid="install-dismiss-btn" aria-label="Chiudi"
          className="absolute top-3.5 right-3.5 h-10 w-10 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors">
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3.5">
          <img src="/icon-192.png" alt="" className="h-14 w-14 rounded-2xl shadow-lg shadow-black/50" />
          <div>
            <h2 className="font-display text-xl font-extrabold text-white leading-tight">
              {installed ? "App già installata" : "Installa Synesis AI"}
            </h2>
            <p className="text-[13px] text-slate-400 mt-0.5">Si apre a tutto schermo, come una vera app</p>
          </div>
        </div>

        {installed ? (
          <div className="mt-6 flex items-center gap-2.5 text-[15px] text-emerald-300">
            <Check className="h-5 w-5" /> Stai già usando Synesis come app installata.
          </div>
        ) : isIOS ? (
          <>
            <div className="mt-5 rounded-2xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-[13.5px] text-amber-100 leading-relaxed" data-testid="install-not-a-button-note">
              Attenzione: l'icona qui sotto è solo un <b>disegno</b>, non un pulsante. Il tasto vero è
              nella <b>barra di Safari</b>, in fondo allo schermo del telefono.
            </div>
            <ol className="mt-5 space-y-5">
              <Step n="1">
                {inChrome
                  ? <>Apri il menu <b className="text-white">⋮</b> in alto a destra in Chrome e scegli <b className="text-white">Condividi</b>.</>
                  : <>Tocca <Share className="h-5 w-5 inline -mt-1 mx-0.5 text-cyan-300" /> <b className="text-white">Condividi</b> nella barra di Safari in fondo allo schermo (il quadrato con la freccia in su).</>}
                {!inChrome && (
                  <img src="/guida/barra-safari.jpg" alt="La barra di Safari con l'icona Condividi evidenziata"
                    className="mt-3 w-full max-w-[15rem] rounded-xl ring-1 ring-white/12" />
                )}
              </Step>
              <Step n="2">
                Scorri l'elenco verso il basso e tocca
                <b className="text-white"> «Aggiungi a schermata Home»</b> <Plus className="h-4 w-4 inline -mt-0.5 ml-0.5 text-cyan-300" />
                <img src="/guida/aggiungi-home.jpg" alt="La voce Aggiungi a schermata Home evidenziata"
                  className="mt-3 w-full max-w-[15rem] rounded-xl ring-1 ring-white/12" />
              </Step>
              <Step n="3">
                Tocca <b className="text-white">Aggiungi</b> in alto a destra: l'icona di Synesis compare tra le tue app.
              </Step>
            </ol>
            {!inChrome && (
              <button onClick={showBrowserBar} data-testid="show-safari-bar-btn"
                className="w-full mt-5 h-12 rounded-xl border border-cyan-400/35 bg-cyan-500/10 text-cyan-100 hover:bg-cyan-500/20 hover:text-white transition-colors flex items-center justify-center gap-2 text-[14.5px] font-medium">
                <ArrowDown className="h-4 w-4" /> Non vedo la barra di Safari
              </button>
            )}
            <div className="mt-4 rounded-2xl border border-cyan-400/25 bg-cyan-500/10 px-4 py-3 text-[13px] text-cyan-100 leading-relaxed">
              Su iPhone funziona sia da <b>Safari</b> sia da <b>Chrome</b>. Se stai usando il browser dentro un'altra app (WhatsApp, Instagram…), passa a uno dei due con i pulsanti qui sotto.
            </div>
            <div className="mt-4 grid grid-cols-1 gap-2.5">
              {!inSafari && (
                <Button onClick={openInSafari} data-testid="open-in-safari-btn"
                  className="w-full h-12 text-[15px] bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white">
                  <Compass className="h-5 w-5 mr-2" /> Apri in Safari
                </Button>
              )}
              {!inChrome && (
                <Button onClick={openInChrome} data-testid="open-in-chrome-btn"
                  className="w-full h-12 text-[15px] bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white">
                  <Chrome className="h-5 w-5 mr-2" /> Apri in Chrome
                </Button>
              )}
            </div>
            <button onClick={copyLink} data-testid="copy-link-btn"
              className="w-full mt-3 h-11 rounded-md text-[14px] text-slate-300 hover:text-white hover:bg-white/[0.06] transition-colors flex items-center justify-center gap-2">
              <Link2 className="h-4 w-4" /> Copia il link da incollare nel browser
            </button>
            <Button onClick={close} data-testid="install-understood-btn"
              className="w-full mt-3 h-12 text-[15px] bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
              Ho capito
            </Button>
          </>
        ) : deferred ? (
          <>
            <p className="mt-6 text-[15px] leading-7 text-slate-300">
              Tocca il pulsante qui sotto e conferma: Synesis viene aggiunta alla schermata home del telefono.
            </p>
            <Button onClick={install} data-testid="install-accept-btn"
              className="w-full mt-6 h-12 text-[15px] bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
              <Download className="h-5 w-5 mr-2" /> Installa adesso
            </Button>
            <button onClick={close} data-testid="install-later-btn"
              className="w-full mt-3 h-11 text-[14px] text-slate-400 hover:text-white transition-colors">
              Più tardi
            </button>
          </>
        ) : (
          <>
            <ol className="mt-7 space-y-5">
              <Step n="1">
                Apri questa pagina dal <b className="text-white">telefono</b> <Smartphone className="h-4 w-4 inline -mt-0.5 ml-0.5 text-cyan-300" /> con Chrome (Android) o Safari/Chrome (iPhone).
              </Step>
              <Step n="2">
                Apri il menu del browser (i <b className="text-white">tre puntini</b> in alto) oppure
                <Share className="h-5 w-5 inline -mt-1 mx-0.5 text-cyan-300" /> <b className="text-white">Condividi</b> su iPhone.
              </Step>
              <Step n="3">
                Scegli <b className="text-white">«Installa app»</b> o <b className="text-white">«Aggiungi a schermata Home»</b>.
              </Step>
            </ol>
            {!inChrome && (
              <Button onClick={openInChrome} data-testid="open-in-chrome-btn"
                className="w-full mt-5 h-12 text-[15px] bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white">
                <Chrome className="h-5 w-5 mr-2" /> Apri in Chrome
              </Button>
            )}
            <button onClick={copyLink} data-testid="copy-link-btn"
              className="w-full mt-3 h-11 rounded-md text-[14px] text-slate-300 hover:text-white hover:bg-white/[0.06] transition-colors flex items-center justify-center gap-2">
              <Link2 className="h-4 w-4" /> Copia il link da incollare nel browser
            </button>
            <Button onClick={close} data-testid="install-understood-btn"
              className="w-full mt-3 h-12 text-[15px] bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
              Ho capito
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
